/**
 * Coach "personal info" schema + server functions.
 *
 * Historically these lived inside the legacy GHL personal-info route, which
 * has since been retired (it now redirects to the Clerk-based `/my-coaching`
 * experience). The shared schema and server functions are housed here as
 * reusable functions per the server-function architecture convention.
 *
 * Aliases: coach personal info, personal info server functions, account info
 */

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireCoachAuth } from "~/lib/coach-middleware";

export const PersonalInfoSchema = z.object({
  id: z.string(),
  username: z.string(),
  fullName: z.string().min(2),
  phone: z.string().min(1),

  profileId: z.string().nullable().optional(),
  profile: z.object({
    backgroundInformation: z.string().max(2000).optional(),
    primaryCity: z.string().min(2),
    primaryCountry: z.string().min(2),
    closestMajorCity: z.string(),
  }),
});

export type PersonalInfoFormData = z.infer<typeof PersonalInfoSchema>;

export type PersonalInfoCoachUpdate = Pick<
  PersonalInfoFormData,
  "fullName" | "phone"
>;

/**
 * Build the editable coach-column payload for personal info saves. The email
 * is not editable here: it changes only once verified, through
 * `changeAccountEmail` in account settings.
 *
 * Aliases: coach personal info update, personal info editable fields
 */
export const getPersonalInfoCoachUpdate = (
  data: PersonalInfoFormData,
): PersonalInfoCoachUpdate => ({
  fullName: data.fullName,
  phone: data.phone,
});

export const getPersonalInfo = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async ({ context }) => {
    const { ensureCoachProfile } = await import("~/lib/server/coach");
    const coach = context.coach;
    const profile = await ensureCoachProfile(coach.id, coach.profileId);

    if (!profile) {
      throw new Error(`Profile not found for coach ${coach.id}`);
    }

    return {
      id: coach.id,
      username: coach.username,
      fullName: coach.fullName,
      email: coach.email,
      phone: coach.phone ?? "",
      profileId: profile.id,
      profile: {
        primaryCity: profile.primaryCity ?? "",
        primaryCountry: profile.primaryCountry ?? "",
        backgroundInformation: profile.backgroundInformation ?? "",
        closestMajorCity: profile.closestMajorCity ?? "",
      },
    };
  });

export const getCityOptions = createServerFn({ method: "GET" })
  .middleware([requireCoachAuth])
  .handler(async () => {
    const { getAllCitiesGrouped } = await import("~/lib/server/cities");
    return getAllCitiesGrouped();
  });

export const savePersonalInfo = createServerFn({ method: "POST" })
  .middleware([requireCoachAuth])
  .validator(PersonalInfoSchema)
  .handler(async ({ data, context }) => {
    const { updateCoach, updateCoachProfile, ensureCoachProfile } =
      await import("~/lib/server/coach");

    const coachId = context.coach.id;
    const profile = await ensureCoachProfile(coachId, context.coach.profileId);

    // `username` is intentionally immutable here — a coach profile is served
    // at the top-level `/$username` route (sharing the namespace with system
    // routes like `/about` and `/nova`), so it must not be changeable through
    // this form (which sends it back as a hidden field). Other server-owned
    // identifiers in the form are ignored for the same reason.
    await updateCoach(coachId, getPersonalInfoCoachUpdate(data));
    if (profile) {
      await updateCoachProfile(profile.id, data.profile);
    }

    return {
      id: coachId,
      username: context.coach.username,
      fullName: data.fullName,
      email: context.coach.email,
      phone: data.phone,
      profileId: profile?.id ?? null,
      profile: {
        primaryCity: data.profile.primaryCity,
        primaryCountry: data.profile.primaryCountry,
        backgroundInformation: data.profile.backgroundInformation ?? "",
        closestMajorCity: data.profile.closestMajorCity,
      },
    };
  });
